import type { ComponentProps, ReactNode } from "react";
import { Link } from "expo-router";
import { Pressable, StyleSheet, Text } from "react-native";

import { fonts, useThemedStyles, type ThemeColors } from "~/styles/theme";

export function AppLink({
  children,
  size = "xs",
  tone = "default",
  align,
  ...props
}: ComponentProps<typeof Link> & {
  children: ReactNode;
  size?: "xs" | "sm" | "base";
  tone?: "default" | "inverse";
  /** Place the link within its parent column instead of stretching it. */
  align?: "start" | "center" | "end";
}) {
  const { stylesheet, sizeStyles, alignStyles } = useThemedStyles(makeStyles);
  return (
    <Link accessibilityRole="link" asChild {...props}>
      <Pressable
        style={align ? alignStyles[align] : undefined}
        hitSlop={{ top: 12, bottom: 12, left: 8, right: 8 }}
      >
        {({ hovered, pressed }) => (
          <Text
            style={[
              sizeStyles[size],
              tone === "inverse" ? stylesheet.inverse : stylesheet.default,
              hovered || pressed ? stylesheet.hover : null,
            ]}
          >
            {children}
          </Text>
        )}
      </Pressable>
    </Link>
  );
}

const makeStyles = (colors: ThemeColors) => {
  const stylesheet = StyleSheet.create({
    xs: { fontFamily: fonts.regular, fontSize: 12 },
    sm: { fontFamily: fonts.regular, fontSize: 14 },
    base: { fontFamily: fonts.regular, fontSize: 16 },
    default: { color: colors.primary },
    inverse: { color: "#FFFFFF" },
    hover: { textDecorationLine: "underline" as const },
  });
  const sizeStyles = {
    xs: stylesheet.xs,
    sm: stylesheet.sm,
    base: stylesheet.base,
  };
  const alignStyles = {
    start: { alignSelf: "flex-start" as const },
    center: { alignSelf: "center" as const },
    end: { alignSelf: "flex-end" as const },
  };
  return { stylesheet, sizeStyles, alignStyles };
};
